import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FileOutlined, LoadingOutlined } from "@ant-design/icons";
import theme from "../themes";
import { getFileIcon, mediaKindOf } from "../utils/fileUtils";

export type MessageMediaItem = {
  kind?: string;
  url?: string;
  content_type?: string;
  filename?: string;
  index?: number;
};

type ThreadMediaProps = {
  messageId?: string;
  items?: MessageMediaItem[];
  fallbackUrl?: string;
};

type CachedBlob = { url: string; type: string };

const MAX_BLOB_CACHE = 40;
const blobCache = new Map<string, CachedBlob>();
const inflight = new Map<string, Promise<CachedBlob>>();

const cacheKey = (messageId: string, index: number) => `${messageId}:${index}`;

const asMessageId = (value?: string) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value && "$oid" in (value as object)) {
    return String((value as { $oid: string }).$oid || "");
  }
  return String(value);
};

const getCached = (key: string): CachedBlob | undefined => {
  const value = blobCache.get(key);
  if (!value) return undefined;
  blobCache.delete(key);
  blobCache.set(key, value);
  return value;
};

const setCached = (key: string, packed: CachedBlob) => {
  if (blobCache.has(key)) blobCache.delete(key);
  blobCache.set(key, packed);
  while (blobCache.size > MAX_BLOB_CACHE) {
    const oldest = blobCache.keys().next().value as string | undefined;
    if (!oldest) break;
    const prev = blobCache.get(oldest);
    blobCache.delete(oldest);
    if (prev?.url) URL.revokeObjectURL(prev.url);
  }
};

const loadMediaBlob = (
  messageId: string,
  index: number
): Promise<CachedBlob> => {
  const key = cacheKey(messageId, index);
  const cached = getCached(key);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(key);
  if (pending) return pending;
  const request = axios
    .get(`/ai-channel/media/${messageId}/${index}`, { responseType: "blob" })
    .then((res) => {
      const type = res.data?.type || "";
      if (type.includes("application/json")) {
        throw new Error("not media");
      }
      const packed = { url: URL.createObjectURL(res.data), type };
      setCached(key, packed);
      inflight.delete(key);
      return packed;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });
  inflight.set(key, request);
  return request;
};

const MediaItem: React.FC<{
  messageId?: string;
  index: number;
  item: MessageMediaItem;
}> = ({ messageId, index, item }) => {
  const { t } = useTranslation();
  const hostRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState("");
  const [blobType, setBlobType] = useState("");
  const [failed, setFailed] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const id = asMessageId(messageId);
  const kind = mediaKindOf({
    ...item,
    content_type: blobType || item.content_type,
  });
  const filename =
    item.filename ||
    (kind === "audio"
      ? t("inbound.whatsapp.mediaAudio")
      : kind === "video"
        ? t("inbound.whatsapp.mediaVideo")
        : t("inbound.whatsapp.mediaFile"));

  useEffect(() => {
    const el = hostRef.current;
    if (!el || visible) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setVisible(true);
        observer.disconnect();
      },
      { rootMargin: "240px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    if (id) {
      const cached = getCached(cacheKey(id, index));
      if (cached) {
        setSrc(cached.url);
        setBlobType(cached.type);
        return undefined;
      }
      loadMediaBlob(id, index)
        .then((packed) => {
          if (cancelled) return;
          setSrc(packed.url);
          setBlobType(packed.type);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
      return () => {
        cancelled = true;
      };
    }
    setFailed(true);
    return undefined;
  }, [visible, id, index]);

  const fileFallback = (
    <FileCard
      as={src && !failed ? "a" : "span"}
      href={src && !failed ? src : undefined}
      target="_blank"
      rel="noreferrer"
      download={filename}
    >
      {getFileIcon(blobType || item.content_type || "") || <FileOutlined />}
      <FileMeta>
        <FileName>{filename}</FileName>
        <FileHint>
          {failed || mediaError
            ? t("inbound.whatsapp.mediaUnavailable")
            : t("inbound.whatsapp.openAttachment")}
        </FileHint>
      </FileMeta>
    </FileCard>
  );

  let body: React.ReactNode = <LoadingBox><LoadingOutlined /></LoadingBox>;
  if (!visible) {
    body = <LoadingBox />;
  } else if (failed && !src) {
    body = fileFallback;
  } else if (kind === "image" && src && !mediaError) {
    body = (
      <ImageThumb
        src={src}
        alt={filename}
        onClick={() => window.open(src, "_blank")}
        onError={() => setMediaError(true)}
      />
    );
  } else if (kind === "video" && src && !mediaError) {
    body = (
      <PlayerCard>
        <VideoPlayer
          src={src}
          controls
          playsInline
          preload="metadata"
          onError={() => setMediaError(true)}
        />
        <FileName>{filename}</FileName>
      </PlayerCard>
    );
  } else if (kind === "audio" && src && !mediaError) {
    body = (
      <PlayerCard>
        <AudioRow>
          {getFileIcon("audio/mpeg")}
          <FileName>{filename}</FileName>
        </AudioRow>
        <AudioPlayer
          src={src}
          controls
          preload="metadata"
          onError={() => setMediaError(true)}
        />
      </PlayerCard>
    );
  } else if (src || failed || mediaError) {
    body = fileFallback;
  }

  return <Host ref={hostRef}>{body}</Host>;
};

export const ThreadMedia: React.FC<ThreadMediaProps> = ({
  messageId,
  items,
  fallbackUrl,
}) => {
  const list =
    items && items.length
      ? items
      : fallbackUrl
        ? [{ url: fallbackUrl, kind: "unknown" }]
        : [];
  if (!list.length) return null;
  return (
    <Wrap>
      {list.map((item, index) => (
        <MediaItem
          key={`${asMessageId(messageId) || "media"}-${item.index ?? index}`}
          messageId={messageId}
          index={item.index ?? index}
          item={item}
        />
      ))}
    </Wrap>
  );
};

const Host = styled.div`
  min-width: 260px;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
  min-width: 260px;
`;

const LoadingBox = styled.div`
  width: 260px;
  height: 72px;
  border-radius: 8px;
  background: ${theme.colors.grey3};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.grey1};
`;

const ImageThumb = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  cursor: pointer;
  display: block;

  &:hover {
    opacity: 0.9;
  }
`;

const PlayerCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 260px;
  max-width: 360px;
`;

const AudioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${theme.colors.blue1};

  .anticon {
    font-size: 18px;
  }
`;

const VideoPlayer = styled.video`
  width: 100%;
  min-width: 260px;
  max-height: 320px;
  border-radius: 8px;
  display: block;
  background: #111;
`;

const AudioPlayer = styled.audio`
  width: 100%;
  min-width: 240px;
  height: 40px;
  display: block;
`;

const FileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  color: inherit;
  text-decoration: none;
  background: ${theme.colors.grey4};
  min-width: 220px;
  max-width: 280px;

  .anticon {
    font-size: 20px;
    color: ${theme.colors.blue1};
  }
`;

const FileMeta = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FileName = styled.span`
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #111;
`;

const FileHint = styled.span`
  font-size: 12px;
  color: ${theme.colors.grey1};
`;
